import { useCallback, useEffect, useMemo, useState } from 'react';
import { Title } from 'react-admin';

import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';

import {
    getUltronInsights,
    type UltronInsightsResult,
    type UltronRecentActivity,
} from './ultronInsightsApi';

function utcDate(offsetDays = 0) {
    const date = new Date();
    date.setUTCHours(0, 0, 0, 0);
    date.setUTCDate(date.getUTCDate() + offsetDays);
    return date.toISOString().slice(0, 10);
}

function addUtcDay(date: string) {
    const value = new Date(`${date}T00:00:00.000Z`);
    value.setUTCDate(value.getUTCDate() + 1);
    return value.toISOString();
}

function label(value: string) {
    return value ? value.replaceAll('_', ' ').replace(/\b\w/g, (character) => character.toUpperCase()) : '—';
}

function formatDateTime(value: string) {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function Metric({
    title,
    value,
    detail,
    icon,
}: {
    title: string;
    value: string;
    detail: string;
    icon: React.ReactElement;
}) {
    return (
        <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 3, minWidth: 0 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
                <Box>
                    <Typography variant="overline" color="text.secondary">{title}</Typography>
                    <Typography variant="h4" fontWeight={850}>{value}</Typography>
                </Box>
                <Box sx={{ display: 'grid', placeItems: 'center', width: 40, height: 40, borderRadius: 2, bgcolor: '#eef2ff', color: '#3730a3' }}>
                    {icon}
                </Box>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>{detail}</Typography>
        </Paper>
    );
}

function ActivityCard({ activity }: { activity: UltronRecentActivity }) {
    return (
        <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 2.5 }}>
            <Stack direction="row" justifyContent="space-between" gap={1}>
                <Box minWidth={0}>
                    <Typography fontWeight={800} noWrap>{activity.userName || 'Unnamed user'}</Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>{activity.email || activity.userId}</Typography>
                </Box>
                <Chip size="small" label={activity.activityType === 'chat_turn' ? 'Chat turn' : 'Header click'} />
            </Stack>
            <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 1.25 }}>
                <Chip size="small" variant="outlined" label={label(activity.outcome)} />
                {activity.examName ? <Chip size="small" variant="outlined" label={activity.examName} /> : null}
                {activity.platform ? <Chip size="small" variant="outlined" label={label(activity.platform)} /> : null}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.25 }}>
                {formatDateTime(activity.occurredAtUtc)}
            </Typography>
        </Paper>
    );
}

export function UltronInsightsPage() {
    const [fromDate, setFromDate] = useState(utcDate(-29));
    const [toDate, setToDate] = useState(utcDate());
    const [data, setData] = useState<UltronInsightsResult | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            setData(await getUltronInsights(`${fromDate}T00:00:00.000Z`, addUtcDay(toDate)));
        } catch (reason) {
            setData(null);
            setError(reason instanceof Error ? reason.message : 'Unable to load Ultron insights.');
        } finally {
            setLoading(false);
        }
    }, [fromDate, toDate]);

    useEffect(() => { void load(); }, [load]);

    const peak = useMemo(
        () => Math.max(1, ...(data?.daily.map((day) => Math.max(day.headerClicks, day.chatTurns)) ?? [1])),
        [data],
    );

    const setPreset = (days: number) => {
        setFromDate(utcDate(-(days - 1)));
        setToDate(utcDate());
    };

    return (
        <Box sx={{ p: { xs: 1.5, sm: 2, lg: 3 } }}>
            <Title title="Ultron Insights" />
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} gap={2} sx={{ mb: 2.5 }}>
                <Box>
                    <Typography variant="h4" fontWeight={850}>Ultron Insights</Typography>
                    <Typography color="text.secondary">Ask Ultron header interest, access outcomes, and actual chat usage in one place.</Typography>
                </Box>
                <Button variant="contained" startIcon={loading ? <CircularProgress color="inherit" size={16} /> : <RefreshIcon />} onClick={() => void load()} disabled={loading}>
                    Refresh
                </Button>
            </Stack>

            <Paper variant="outlined" sx={{ p: 2, mb: 2.5, borderRadius: 3 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} gap={1.5} alignItems={{ md: 'center' }}>
                    <Stack direction="row" gap={1} flexWrap="wrap">
                        {[7, 30, 90].map((days) => <Button key={days} size="small" variant="outlined" onClick={() => setPreset(days)}>Last {days} days</Button>)}
                    </Stack>
                    <TextField
                        size="small"
                        type="date"
                        label="From (UTC)"
                        value={fromDate}
                        onChange={(event) => setFromDate(event.target.value)}
                        slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: toDate } }}
                    />
                    <TextField
                        size="small"
                        type="date"
                        label="To (UTC)"
                        value={toDate}
                        onChange={(event) => setToDate(event.target.value)}
                        slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: fromDate, max: utcDate() } }}
                    />
                </Stack>
            </Paper>

            {error ? <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" onClick={() => void load()}>Retry</Button>}>{error}</Alert> : null}
            {loading && !data ? <Box sx={{ py: 10, textAlign: 'center' }}><CircularProgress /></Box> : null}

            {data ? (
                <Stack gap={2.5}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', xl: 'repeat(4, 1fr)' }, gap: 1.5 }}>
                        <Metric
                            title="Ask Ultron clicks"
                            value={data.summary.headerClickUsers.toLocaleString()}
                            detail={`${data.summary.headerClicks.toLocaleString()} total clicks · unique users shown first`}
                            icon={<AutoAwesomeOutlinedIcon />}
                        />
                        <Metric
                            title="Chat users"
                            value={data.summary.chatUsers.toLocaleString()}
                            detail={`${data.summary.chatTurns.toLocaleString()} turns · ${data.summary.clickToChatRate}% clicked then chatted`}
                            icon={<PeopleAltOutlinedIcon />}
                        />
                        <Metric
                            title="Successful chat turns"
                            value={data.summary.successfulTurns.toLocaleString()}
                            detail={`${data.summary.chatSuccessRate}% success · ${data.summary.failedTurns.toLocaleString()} failed`}
                            icon={<ChatBubbleOutlineRoundedIcon />}
                        />
                        <Metric
                            title="Premium redirects"
                            value={data.summary.premiumRedirectClicks.toLocaleString()}
                            detail={`${data.summary.chatOpenedClicks.toLocaleString()} clicks opened chat · ${data.summary.averageTurnsPerChatUser} turns per user`}
                            icon={<LockOpenOutlinedIcon />}
                        />
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.5fr) minmax(320px, 0.8fr)' }, gap: 2 }}>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, minWidth: 0 }}>
                            <Typography variant="h6" fontWeight={800}>Daily activity</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Header clicks and completed Ultron chat turns by UTC day.</Typography>
                            <Stack gap={1.25} sx={{ maxHeight: 420, overflowY: 'auto', pr: 0.5 }}>
                                {data.daily.map((day) => (
                                    <Box key={day.day}>
                                        <Stack direction="row" justifyContent="space-between" gap={1}>
                                            <Typography variant="caption" fontWeight={700}>{day.day}</Typography>
                                            <Typography variant="caption" color="text.secondary">{day.headerClicks} clicks · {day.chatTurns} turns</Typography>
                                        </Stack>
                                        <Stack gap={0.45} sx={{ mt: 0.6 }}>
                                            <Box sx={{ height: 5, borderRadius: 99, bgcolor: '#eef2f7', overflow: 'hidden' }}>
                                                <Box sx={{ width: `${day.headerClicks * 100 / peak}%`, height: '100%', bgcolor: '#6366f1' }} />
                                            </Box>
                                            <Box sx={{ height: 5, borderRadius: 99, bgcolor: '#eef2f7', overflow: 'hidden' }}>
                                                <Box sx={{ width: `${day.chatTurns * 100 / peak}%`, height: '100%', bgcolor: '#14b8a6' }} />
                                            </Box>
                                        </Stack>
                                    </Box>
                                ))}
                                {!data.daily.length ? <Typography color="text.secondary">No daily activity.</Typography> : null}
                            </Stack>
                        </Paper>

                        <Stack gap={2}>
                            <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
                                <Typography variant="h6" fontWeight={800} sx={{ p: 2 }}>By exam</Typography>
                                <Table size="small">
                                    <TableHead><TableRow><TableCell>Exam</TableCell><TableCell align="right">Click users</TableCell><TableCell align="right">Chat users</TableCell></TableRow></TableHead>
                                    <TableBody>
                                        {data.exams.map((exam) => <TableRow key={exam.examName}><TableCell>{exam.examName}</TableCell><TableCell align="right">{exam.headerClickUsers}</TableCell><TableCell align="right">{exam.chatUsers}</TableCell></TableRow>)}
                                        {!data.exams.length ? <TableRow><TableCell colSpan={3} align="center">No activity</TableCell></TableRow> : null}
                                    </TableBody>
                                </Table>
                            </Paper>
                            <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
                                <Typography variant="h6" fontWeight={800} sx={{ p: 2 }}>Click outcomes</Typography>
                                <Table size="small">
                                    <TableHead><TableRow><TableCell>Outcome</TableCell><TableCell align="right">Users</TableCell><TableCell align="right">Clicks</TableCell></TableRow></TableHead>
                                    <TableBody>
                                        {data.entryOutcomes.map((outcome) => <TableRow key={outcome.accessOutcome}><TableCell>{label(outcome.accessOutcome)}</TableCell><TableCell align="right">{outcome.users}</TableCell><TableCell align="right">{outcome.clicks}</TableCell></TableRow>)}
                                        {!data.entryOutcomes.length ? <TableRow><TableCell colSpan={3} align="center">No clicks</TableCell></TableRow> : null}
                                    </TableBody>
                                </Table>
                            </Paper>
                        </Stack>
                    </Box>

                    <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
                        <Box sx={{ p: 2 }}>
                            <Typography variant="h6" fontWeight={800}>Recent Ultron activity</Typography>
                            <Typography variant="body2" color="text.secondary">Latest header clicks and chat turns. Learner messages and Ultron replies are not stored here.</Typography>
                        </Box>
                        <Stack sx={{ display: { xs: 'flex', md: 'none' }, px: 1.5, pb: 1.5 }} gap={1}>
                            {data.recentActivity.map((activity) => <ActivityCard key={`${activity.activityType}-${activity.activityId}`} activity={activity} />)}
                            {!data.recentActivity.length ? <Typography align="center" color="text.secondary" sx={{ py: 4 }}>No Ultron activity in this period.</Typography> : null}
                        </Stack>
                        <TableContainer sx={{ display: { xs: 'none', md: 'block' } }}>
                            <Table size="small" sx={{ minWidth: 980 }}>
                                <TableHead><TableRow><TableCell>Time</TableCell><TableCell>User</TableCell><TableCell>Activity</TableCell><TableCell>Outcome</TableCell><TableCell>Exam</TableCell><TableCell>Source</TableCell><TableCell>Platform</TableCell></TableRow></TableHead>
                                <TableBody>
                                    {data.recentActivity.map((activity) => (
                                        <TableRow key={`${activity.activityType}-${activity.activityId}`}>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(activity.occurredAtUtc)}</TableCell>
                                            <TableCell><Typography variant="body2" fontWeight={750}>{activity.userName || 'Unnamed user'}</Typography><Typography variant="caption" color="text.secondary">{activity.email || activity.userId}</Typography></TableCell>
                                            <TableCell>{activity.activityType === 'chat_turn' ? 'Chat turn' : 'Header click'}</TableCell>
                                            <TableCell><Chip size="small" variant="outlined" label={label(activity.outcome)} /></TableCell>
                                            <TableCell>{activity.examName || '—'}</TableCell>
                                            <TableCell>{label(activity.source)}</TableCell>
                                            <TableCell>{label(activity.platform)}</TableCell>
                                        </TableRow>
                                    ))}
                                    {!data.recentActivity.length ? <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6 }}>No Ultron activity in this period.</TableCell></TableRow> : null}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Stack>
            ) : null}
        </Box>
    );
}
